import React, { useState, useEffect, Fragment } from 'react';

import {
  Box,
  Typography
} from '@mui/material';
import {
  LineChart, Line,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, Customized
} from 'recharts';

const YTDChartComponent = ({ chartRef, title, ytdData, exportMode, showPinnedBox=false }) => {
    const FORECAST_COLOR = '#2e7d32';
    const YTD_COLOR = '#1565c0';
    const BUDGET_COLOR = '#9c27b0';

    const [ytdMetric, setYtdMetric] = React.useState('value');
    const [ytdTotals, setYtdTotals] = React.useState({ ytd_total: 0, eoy_total_forecast: 0 });
    const [monthlyBudgets, setMonthlyBudgets] = React.useState([]);
    const [fyBudget, setFYBudget] = React.useState(0);
    const [data, setData] = useState([]);
    const [ytdAsOf, setYtdAsOf] = React.useState('');

    const ordinal = (n) => { const s=['th','st','nd','rd'], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
    const fmtGBP = (n) => `£${Math.round(n||0).toLocaleString('en-GB')}`;
    const fmtUnits = (n) => `${Math.round(n||0).toLocaleString('en-GB')}`;
    const labelFmt = (iso) => {
        try {
        const d = new Date(iso + 'T00:00:00');
        return d.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
        } catch { return iso; }
    };

    useEffect(() => { 
        if (!ytdData || ytdData.length == 0) return;
        const forecastParsed = Array.isArray(ytdData.points) ? ytdData.points.map(p => ({
            date: p.date, ytd: p.ytd, eoy_forecast: p.eoy_forecast
        })) : [];
        setYtdAsOf(ytdData.month.month_end);
        setData(forecastParsed);
        setYtdMetric(ytdData.metric);
        setYtdTotals({
            ytd_total: Number(ytdData.ytd_total || 0),
            eoy_total_forecast: Number(ytdData.eoy_total_forecast ?? ytdData.eoy_total ?? 0),
        });
        setMonthlyBudgets(Array.isArray(ytdData.monthly_budgets?.monthly) ? ytdData.monthly_budgets.monthly : []);
        setFYBudget(ytdData.monthly_budgets.annual_total);
    }, [ytdData]);

    const ytdMonthDayCounts = React.useMemo(() => {
        const counts = {};
        for (const p of data || []) {
        if (!p?.date) continue;
        const m = String(p.date).slice(0, 7);
        counts[m] = (counts[m] || 0) + 1;
        }
        return counts;
    }, [data]);

    // Build a map: date -> cumulative budget up to that date
    const budgetCurveMap = React.useMemo(() => {
        if (!data?.length || !monthlyBudgets?.length) return null;
        const budgetByMonth = monthlyBudgets.reduce((acc, b) => {
        const m = String(b.month).slice(0, 7);          // "YYYY-MM" whether you pass "YYYY-MM" or "YYYY-MM-01"
        const v = Number(b.budget) || 0;
        acc.set(m, (acc.get(m) || 0) + v);              // accumulate
        return acc;
        }, new Map());

        const ytdSorted = [...data].sort((a, b) => String(a.date).localeCompare(String(b.date)));

        let cum = 0;
        const perDayCache = {};
        const map = {};
        for (const p of ytdSorted) {
        const d = p.date; if (!d) continue;
        const m = String(d).slice(0, 7);
        if (!(m in perDayCache)) {
            const monthBudget = budgetByMonth.get(m) || 0;
            const nDays = ytdMonthDayCounts[m] || 1;
            perDayCache[m] = monthBudget / nDays;
        }
        cum += perDayCache[m];
        map[d] = cum;
        }
        return map;
    }, [data, monthlyBudgets, ytdMonthDayCounts]);

    const ytdChartData = React.useMemo(() => {
        if (!data?.length) return [];
        if (!budgetCurveMap) return data;
        return data.map(p => ({ ...p, budget_cum: budgetCurveMap[p.date] ?? null }));
    }, [data, budgetCurveMap]);

    const monthlyBudget = React.useMemo(() => {
        if (!ytdAsOf?.length) return 0;
        if (!budgetCurveMap) return 0;
        return budgetCurveMap[ytdAsOf];
    }, [ytdAsOf, budgetCurveMap]);

    const todayISO = ytdAsOf || '';
    const todayIndex = ytdChartData.findIndex(p => p.date === todayISO);
    const CHART_HEIGHT = 420;
    const selected = todayIndex >= 0 ? ytdChartData[todayIndex] : null;

    const rows = [
        { key: 'ytd',          color: YTD_COLOR, label: 'YTD',           value: selected?.ytd },
        { key: 'eoy_forecast', color: FORECAST_COLOR, label: 'EOY Forecast', value: selected?.eoy_total_forecast },
        { key: 'budget_cum',   color: BUDGET_COLOR, label: 'Budget (YTD)', value: selected?.budget_cum },
    ].filter(r => r.value != null);

    return (
        <Box ref={chartRef} sx={{ width: exportMode ? 1000 : '100%' }} padding={2}>
            <Typography variant="title" sx={{ fontWeight:500 }}>
                {title}
            </Typography>
            <Box sx={{ display:'flex', flexWrap:'wrap', gap: 1.25, mb: 1.5 }}>
                {[
                { color: YTD_COLOR, label: 'YTD', value: ytdTotals.ytd_total },
                { color: BUDGET_COLOR, label: 'YTD Budget', value: monthlyBudget },
                { color: FORECAST_COLOR, label: 'EOY Forecast', value: ytdTotals.eoy_total_forecast },
                { color: BUDGET_COLOR, label: 'FY Budget', value: fyBudget },
                ].map((r, i) => (
                <Box
                    key={i}
                    sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 999,
                    bgcolor: 'background.paper',
                    }}
                    title={`${r.label}: ${ytdMetric === 'value' ? fmtGBP(r.value) : fmtUnits(r.value)}`}
                >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color, flex: '0 0 auto' }} />
                    <Typography variant="body2">{r.label}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight:700 }}>
                    {ytdMetric === 'value' ? fmtGBP(r.value) : fmtUnits(r.value)}
                    </Typography>
                </Box>
                ))}
            </Box>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart data={ytdChartData} margin={{ top: 8, right: 12, bottom: 8, left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={labelFmt} minTickGap={28} tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v)=> ytdMetric === 'value' ? fmtGBP(v) : fmtUnits(v)} tick={{ fontSize: 12 }} />
                <RTooltip
                    formatter={(val, _name, props) => {
                    const key = props?.dataKey;
                    const nice =
                        key === 'ytd' ? 'YTD'
                        : key === 'eoy_forecast' ? 'EOY Forecast'
                        : key === 'budget_cum' ? 'Budget (YTD)'
                        : key;
                    return [ytdMetric === 'value' ? fmtGBP(val) : fmtUnits(val), nice];
                    }}
                    labelFormatter={(iso)=> `Date: ${labelFmt(iso)}`}
                />
                <Line type="monotone" dataKey="ytd" name="YTD" stroke={YTD_COLOR} strokeWidth={2.25} dot={false} isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
                <Line type="monotone" dataKey="eoy_forecast" name="EOY Forecast" stroke={FORECAST_COLOR} strokeDasharray="6 4" strokeWidth={2.25} dot={false} connectNulls isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
                <Line type="monotone" dataKey="budget_cum" name="Budget (YTD)" stroke={BUDGET_COLOR} strokeDasharray="2 2" strokeWidth={2} dot={false} connectNulls isAnimationActive={!exportMode} animationDuration={exportMode ? 0 : 400} />
                {!exportMode && todayIndex >= 0 && (<ReferenceLine x={todayISO} stroke="#9e9e9e" strokeDasharray="3 3" />)}
                {exportMode && showPinnedBox && selected && (
                    <ReferenceLine
                    x={selected.date}
                    stroke="#999"
                    strokeDasharray="4 4"
                    ifOverflow="extendDomain"
                    />
                )}
                {exportMode && showPinnedBox && selected && (
                    <Customized
                    component={
                        <PinnedBox
                        point={selected}
                        metric={ytdMetric}
                        fmtGBP={fmtGBP}
                        fmtUnits={fmtUnits}
                        rows={rows}
                        />
                    }
                    />
                )}
                </LineChart>
            </ResponsiveContainer>
            <Typography variant="subtitle2" sx={{ fontWeight:500 }}>
                EOY Forecast predicted by RxAI
            </Typography>
        </Box>
    );

    function PinnedBox({ point, metric, fmtGBP, fmtUnits, rows, ...ctx }) {
        const { offset, xAxisMap, chartWidth = 0 } = ctx;
        const xAxis = Object.values(xAxisMap)[0];

        const anchorX = Math.round(xAxis?.scale?.(point.date) ?? 0) + (offset?.left ?? 0);
        const boxWidth = 220;
        const padding = 8;

        let boxX = anchorX + 60;
        const chartLeft = (offset?.left ?? 0);
        const chartRight = (offset?.left ?? 0) + (ctx?.chartWidth ?? 0);

        if (boxX + boxWidth + padding > chartRight) {
            boxX = anchorX - 60 - boxWidth;
        }

        if (boxX < chartLeft + padding) {
            boxX = chartLeft + padding;
        }

        const boxY = (offset?.top ?? 0) + 12;
        const fmt = (v) => (metric === 'value' ? fmtGBP(v) : fmtUnits(v));

        return (
            <g>
            <foreignObject x={boxX} y={boxY} width={boxWidth} height="200">
                <div
                style={{
                    background: 'var(--mui-palette-background-paper, #fff)',
                    border: '1px solid var(--mui-palette-divider, #e0e0e0)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    borderRadius: 8,
                    padding: 10,
                    fontFamily: 'inherit',
                    fontSize: 12,
                    lineHeight: 1.4,
                }}
                >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Date: {labelFmt(point.date)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6 }}>
                    {rows.map(r => (
                    <Fragment key={r.key}>
                        <span style={{ width: 10, height: 10, borderRadius: 9999, background: r.color, display: 'inline-block', marginTop: 4 }} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {r.label} </span>
                        <strong>{fmt(r.value)}</strong>
                    </Fragment>
                    ))}
                </div>
                </div>
            </foreignObject>
            </g>
        );
    }
}

export default YTDChartComponent;